import { logger } from "@/lib/logger";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface PatientVisit {
  id: string;
  patient_id: string;
  trial_site_id: string;
  visit_type: string;
  visit_number: number;
  scheduled_date: string;
  actual_date?: string;
  visit_status: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export function usePatientVisitsForTrial(trialId: string, trialSiteId: string) {
  return useQuery({
    queryKey: ['patient-visits-trial', trialId, trialSiteId],
    queryFn: async () => {
      // Get patient IDs assigned to this trial at this site
      const { data: patientStatuses, error: ptsError } = await supabase
        .from('patient_trial_status')
        .select('patient_id')
        .eq('trial_id', trialId)
        .eq('trial_site_id', trialSiteId);

      if (ptsError) throw ptsError;
      if (!patientStatuses || patientStatuses.length === 0) return [];

      const patientIds = patientStatuses.map(p => p.patient_id);

      // Get visits for these patients at this site
      const { data, error } = await supabase
        .from('patient_visits')
        .select(`
          *,
          profiles!patient_visits_patient_id_fkey(
            id,
            first_name,
            last_name,
            patient_number
          )
        `)
        .eq('trial_site_id', trialSiteId)
        .in('patient_id', patientIds)
        .order('scheduled_date', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!trialId && !!trialSiteId,
    staleTime: 30 * 1000,
  });
}

export function usePatientVisits(patientId?: string, trialSiteId?: string) {
  return useQuery({
    queryKey: ['patient-visits', patientId, trialSiteId],
    queryFn: async () => {
      if (!patientId) return [];

      let query = supabase
        .from('patient_visits')
        .select(`
          *,
          conductor:conducted_by(first_name, last_name)
        `)
        .eq('patient_id', patientId)
        .order('scheduled_date', { ascending: true });

      if (trialSiteId) {
        query = query.eq('trial_site_id', trialSiteId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as PatientVisit[];
    },
    enabled: !!patientId,
    staleTime: 30 * 1000,
  });
}

// Re-export the billing-integrated useUpdateVisitStatus from useVisitManagement
// This ensures all visit status updates trigger auto-billing
export { useUpdateVisitStatus } from './useVisitManagement';

export function useRescheduleVisit() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      visitId, 
      newDate,
      notes
    }: { 
      visitId: string; 
      newDate: string;
      notes?: string;
    }) => {
      const { data, error } = await supabase
        .from('patient_visits')
        .update({
          scheduled_date: newDate,
          visit_status: 'rescheduled',
          notes: notes || 'Visit rescheduled',
          updated_at: new Date().toISOString(),
        })
        .eq('id', visitId)
        .select()
        .single();

      if (error) throw error;
      return data as PatientVisit;
    },
    onSuccess: async (data) => {
      // Log to audit trail
      try {
        const { data: currentUser } = await supabase.auth.getUser();
        if (currentUser?.user?.id) {
          await supabase.from('audit_trails').insert({
            user_id: currentUser.user.id,
            action: 'visit_rescheduled',
            entity_type: 'patient_visit',
            entity_id: data.id,
            details: {
              visit_type: data.visit_type,
              new_scheduled_date: data.scheduled_date,
              patient_id: data.patient_id,
              trial_site_id: data.trial_site_id,
              notes: data.notes,
            },
            risk_level: 'low',
          });
        }
      } catch (auditError) {
        logger.error('Failed to log reschedule audit trail:', auditError);
      }

      queryClient.invalidateQueries({ queryKey: ['patient-visits'] });
      queryClient.invalidateQueries({ queryKey: ['patient-visits-trial'] });
      queryClient.invalidateQueries({ queryKey: ['site-visits-calendar'] });
      queryClient.invalidateQueries({ queryKey: ['trial-site-visits'] });

      // Queue reschedule notification to patient
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('user_id, first_name, last_name, whatsapp_opted_in')
          .eq('id', data.patient_id)
          .single();

        const { data: site } = await supabase
          .from('trial_sites_safe')
          .select('name')
          .eq('id', data.trial_site_id)
          .single();

        if (profile && site) {
          const { data: template } = await supabase
            .from('notification_templates')
            .select('id')
            .eq('notification_type', profile.whatsapp_opted_in ? 'visit_rescheduled_whatsapp' : 'visit_rescheduled_sms')
            .eq('is_active', true)
            .single();

          const newDate = new Date(data.scheduled_date);
          const formattedDate = newDate.toLocaleDateString('en-US', {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
          });

          await supabase
            .from('notification_queue')
            .insert([{
              user_id: profile.user_id,
              template_id: template?.id || null,
              channel: profile.whatsapp_opted_in ? 'whatsapp' : 'sms',
              priority: 2,
              variables: {
                patient_name: `${profile.first_name} ${profile.last_name}`,
                visit_type: data.visit_type.replace(/_/g, ' ').toUpperCase(),
                new_visit_date: formattedDate,
                site_name: site.name,
                fallback_content: `Dear ${profile.first_name}, your ${data.visit_type.replace(/_/g, ' ')} visit has been rescheduled to ${formattedDate} at ${site.name}. Please contact us if this does not work for you.`
              },
              metadata: {
                visit_id: data.id,
                trial_site_id: data.trial_site_id,
                notification_type: 'visit_rescheduled'
              },
              scheduled_for: new Date().toISOString(),
              status: 'pending'
            }]);
        }
      } catch (notifError) {
        logger.error('Failed to queue reschedule notification:', notifError);
      }

      toast({
        title: "Success",
        description: "Visit rescheduled",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to reschedule visit",
        variant: "destructive",
      });
    },
  });
}
